import { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useBudgetCalculations } from '@/hooks/useBudgetCalculations';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { Plus, Trash2, AlertCircle, MoveHorizontal, ArrowLeftRight, Files } from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { formatHours, formatCurrency } from '@/lib/budget-utils';
import { fetchRoles, type RoleOption } from '@/lib/data-service';
import type { ResourceBudget, RoleBudget, PersonBudget, PhaseBudgetResource } from '@/types/budget.types';
import { FillDialog, type FillType } from './fill-dialog'; // Original modal implementation (preserved)
import { FillSheet } from './fill-sheet'; // New sheet implementation with expand/collapse
import { AISuggestion } from './ai-suggestion';
import { ShiftBudgetSheet } from './shift-budget-sheet';
// import { ShiftBudgetDialog } from './shift-budget-dialog'; // Original modal - preserved for reference
// Drag and drop imports removed - reordering disabled
// import {
//   DndContext,
//   closestCenter,
//   PointerSensor,
//   useSensor,
//   useSensors,
//   type DragEndEvent,
// } from '@dnd-kit/core';
// import {
//   arrayMove,
//   SortableContext,
//   useSortable,
//   verticalListSortingStrategy,
// } from '@dnd-kit/sortable';
// import { CSS } from '@dnd-kit/utilities';

interface EditResourceBudgetFormProps {
  projectId: number;
  resourceBudget: ResourceBudget;
  onChange: (changes: ResourceBudget) => void;
  selectedYear?: string;
  projectStartDate?: string;
  projectEndDate?: string;
}

// Fallback role options used only when API fetch fails
const FALLBACK_ROLE_OPTIONS = [
  'Project Manager',
  'Senior Consultant',
  'Consultant',
  'Developer',
  'Senior Developer',
  'QA Analyst',
  'QA Engineer',
  'Business Analyst',
  'Technical Architect',
  'UX Designer',
  'Scrum Master',
];

// Month names for columns
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// NumberInput component that handles 2dp formatting properly with max value validation
const NumberInput = ({ value, onChange, maxValue = 999999.99, onFocus, ...props }: { value: number; onChange: (value: number) => void; maxValue?: number; onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void; [key: string]: any }) => {
  const [displayValue, setDisplayValue] = useState(value.toFixed(2));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value.toFixed(2));
    }
  }, [value, isFocused]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // Select all text on focus for easy editing
    e.target.select();
    // Call parent's onFocus if provided
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Clear any pending debounced update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    let numValue = parseFloat(displayValue) || 0;
    
    // Enforce maximum value
    if (numValue > maxValue) {
      numValue = maxValue;
    }
    
    const formattedValue = parseFloat(numValue.toFixed(2));
    setDisplayValue(formattedValue.toFixed(2));
    // Only call onChange if the value actually changed to avoid false "unsaved changes"
    if (formattedValue !== value) {
      onChange(formattedValue);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Allow typing numbers, decimal point, and empty string
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setDisplayValue(val);
      
      // Debounced onChange to enable toolbar buttons while typing
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set a new debounced timer (300ms delay to allow for slow typing)
      debounceTimerRef.current = setTimeout(() => {
        let numValue = parseFloat(val) || 0;
        // Enforce maximum value
        if (numValue > maxValue) {
          numValue = maxValue;
        }
        const formattedValue = parseFloat(numValue.toFixed(2));
        // Only call onChange if the value actually changed
        if (formattedValue !== value) {
          onChange(formattedValue);
        }
      }, 300);
    }
  };

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

// Helper function to format numbers with thousand separators
const formatNumber = (value: number): string => {
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseNumber = (value: string): number => {
  // Remove any commas and parse as float
  const cleaned = value.replace(/,/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

// Props interface for SortableRow - moved outside to prevent recreation on every render
interface SortableRowProps {
  role: RoleBudget;
  index: number;
  selectedCell: { roleIndex: number; monthIndex: number | null } | null;
  setSelectedCell: (cell: { roleIndex: number; monthIndex: number | null } | null) => void;
  setSelectedRows: (rows: Set<number>) => void;
  updateRole: (index: number, field: keyof RoleBudget, value: any) => void;
  updateMonthlyHours: (roleIndex: number, monthIndex: number, value: number) => void;
  handleFillClick: (roleIndex: number, monthIndex: number) => void;
  isSelected: boolean;
  onToggleSelection: (roleId: number) => void;
  visibleMonths: number[];
}

// Regular Row component - drag and drop removed
const RegularRow = memo(function RegularRow({
  role,
  index,
  selectedCell,
  setSelectedCell,
  setSelectedRows,
  updateRole,
  updateMonthlyHours,
  handleFillClick,
  isSelected,
  onToggleSelection,
  visibleMonths,
}: SortableRowProps) {
  // Get monthlyHours array - should already be properly sized from initialization
  const monthlyHours = role.monthlyHours || [];
  const isCellSelected = selectedCell?.roleIndex === index;

  return (
    <TableRow 
      className={`group ${isCellSelected ? 'bg-primary/5' : ''} ${isSelected ? 'bg-primary/10' : ''}`}
    >
      {/* Checkbox - Frozen */}
      <TableCell className={`px-2 py-1 w-[40px] sticky left-0 z-10 ${isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'}`}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelection(role.id)}
          aria-label={`Select ${role.role}`}
          onClick={(e) => e.stopPropagation()}
        />
      </TableCell>
      
      {/* Role Dropdown - Frozen */}
      <TableCell className={`px-2 py-1 w-[200px] sticky left-[40px] z-10 border-r ${isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'}`}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Combobox
                  options={(() => {
                    // Include the current role if it's not in the predefined list
                    const options = FALLBACK_ROLE_OPTIONS.map((option) => ({
                      value: option,
                      label: option,
                    }));
                    if (role.role && !FALLBACK_ROLE_OPTIONS.includes(role.role)) {
                      options.unshift({ value: role.role, label: role.role });
                    }
                    return options;
                  })()}
                  value={role.role}
                  onValueChange={(value) => updateRole(index, 'role', value)}
                  placeholder="Select role"
                  searchPlaceholder="Search roles..."
                  emptyMessage="No role found."
                  className="h-9"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{role.role}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>

      {/* Monthly Hours */}
      {visibleMonths.map((monthIndex) => {
        const hours = monthlyHours[monthIndex] || 0;
        const isCellSelected = isSelected && selectedCell?.monthIndex === monthIndex;
        return (
          <TableCell 
            key={monthIndex} 
            className="p-2 w-[100px]"
          >
            <div 
              className="relative group/cell"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                setSelectedCell({ roleIndex: index, monthIndex });
                setSelectedRows(new Set()); // Clear row selection when selecting a cell
              }}
            >
              <NumberInput
                value={hours}
                onChange={(value) => updateMonthlyHours(index, monthIndex, value)}
                maxValue={999999.99}
                className={`w-full h-9 px-2 text-sm text-center ${
                  selectedCell?.roleIndex === index && selectedCell?.monthIndex === monthIndex ? 'ring-2 ring-primary' : ''
                }`}
                placeholder="0.00"
              />
              {/* Fill Right Arrow - COMMENTED OUT - Moved to toolbar button */}
              {/* Validation: hours > 0 && monthIndex < totalMonths - 1 */}
              {/* {hours > 0 && monthIndex < totalMonths - 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover/cell:opacity-100 transition-opacity absolute -right-7 top-1/2 -translate-y-1/2"
                  onClick={() => handleFillClick(index, monthIndex)}
                  title="Fill right"
                  tabIndex={-1}
                >
                  <ArrowRight className="h-3 w-3" />
                </Button>
              )} */}
            </div>
          </TableCell>
        );
      })}

      {/* Project Total - Frozen to Right */}
      <TableCell className={`p-2 w-[120px] border-l-2 border-border ${isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'}`}>
        <Input
          value={formatNumber(role.budgetHours)}
          readOnly
          className="w-full h-9 px-2 text-sm text-center font-semibold bg-primary/10 cursor-default"
          title={formatNumber(role.budgetHours)}
        />
      </TableCell>
    </TableRow>
  );
});

export function EditResourceBudgetForm({
  projectId,
  resourceBudget,
  onChange,
  selectedYear,
  projectStartDate,
  projectEndDate,
}: EditResourceBudgetFormProps) {
  // Get rate calculations from Rate Card
  const { roleBreakdown } = useBudgetCalculations(String(projectId), resourceBudget);

  // Create a map of role names to their bill rates from Rate Card
  const roleRateMap = useMemo(() => {
    const map = new Map<string, number>();
    roleBreakdown.forEach(role => {
      map.set(role.roleName, role.averageBillRate);
    });
    console.log('[EditResourceBudgetForm] Role rate map created:', Array.from(map.entries()));
    return map;
  }, [roleBreakdown]);

  // Helper function to get bill rate from Rate Card
  const getRoleBillRate = (roleName: string, fallbackRate: number = 150): number => {
    const rateFromCard = roleRateMap.get(roleName);
    if (rateFromCard !== undefined) {
      console.log('[EditResourceBudgetForm] Using Rate Card rate for role:', roleName, '=', rateFromCard);
      return rateFromCard;
    }
    console.warn('[EditResourceBudgetForm] No rate found in Rate Card for role:', roleName, 'using fallback:', fallbackRate);
    return fallbackRate;
  };

  // Calculate all available months for the project across all years
  const allProjectMonths = (() => {
    if (!projectStartDate || !projectEndDate) {
      // Show all months of current year if no project dates
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 12 }, (_, i) => ({ year: currentYear, month: i }));
    }
    
    const startDate = new Date(projectStartDate);
    const endDate = new Date(projectEndDate);
    
    const months: { year: number; month: number }[] = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    
    while (current <= end) {
      months.push({ year: current.getFullYear(), month: current.getMonth() });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  })();

  // Show ALL months for the entire project duration - no year filtering
  const yearMonths = allProjectMonths;

  // Debug logging
  console.log('[EditResourceBudgetForm] Project dates:', { projectStartDate, projectEndDate });
  console.log('[EditResourceBudgetForm] Total months to display:', yearMonths.length);
  console.log('[EditResourceBudgetForm] Month range:', yearMonths.length > 0 ? `${MONTHS[yearMonths[0].month]} ${yearMonths[0].year} - ${MONTHS[yearMonths[yearMonths.length - 1].month]} ${yearMonths[yearMonths.length - 1].year}` : 'None');

  // Create a mapping from global month index to year/month
  // This allows us to work with a flat array while preserving year information
  const monthIndexMap = yearMonths.map((ym, index) => ({
    globalIndex: index,
    year: ym.year,
    month: ym.month,
    yearMonthKey: `${ym.year}-${ym.month}` // Unique key for this month
  }));

  // Show ALL months - no dynamic loading
  const visibleMonths = monthIndexMap.map(m => m.globalIndex);
  
  // Total number of months to display
  const totalMonths = yearMonths.length;

  // No dynamic loading needed - all months are rendered from the start
  
  // Initialize byRole with properly sized monthlyHours arrays
  const [byRole, setByRole] = useState<RoleBudget[]>(() => {
    const roles = resourceBudget?.byRole || [];
    
    return roles.map(role => ({
      ...role,
      monthlyHours: role.monthlyHours 
        ? [...role.monthlyHours, ...Array(Math.max(0, totalMonths - role.monthlyHours.length)).fill(0)]
        : Array(totalMonths).fill(0)
    }));
  });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [deleteAttempt, setDeleteAttempt] = useState<{ index: number; role: RoleBudget; rolesWithHours?: RoleBudget[] } | null>(null);
  const [byPerson, setByPerson] = useState<PersonBudget[]>(resourceBudget?.byPerson || []);
  const [byPhase, setByPhase] = useState<PhaseBudgetResource[]>(resourceBudget?.byPhase || []);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  
  // Fill dialog state
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillSource, setFillSource] = useState<{ roleIndex: number; monthIndex: number; value: number } | null>(null);
  
  // AI suggestion state
  const [patternSuggestion, setPatternSuggestion] = useState<{
    roleIndex: number;
    value: number;
    fromMonth: number;
    toMonth: number;
  } | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  // Clipboard state for copy/paste
  const [clipboard, setClipboard] = useState<{
    data: number | number[] | null;
    type: 'cell' | 'row' | null;
    sourceRoleIndex: number | null;
    sourceMonthIndex: number | null;
    isCut: boolean;
  }>({ data: null, type: null, sourceRoleIndex: null, sourceMonthIndex: null, isCut: false });

  // Selection state - can be a single cell or entire row
  const [selectedCell, setSelectedCell] = useState<{
    roleIndex: number;
    monthIndex: number | null; // null means entire row selected
  } | null>(null);

  // Shift budget dialog state
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftSource, setShiftSource] = useState<{ roleIndex: number; roleName: string } | null>(null);

  // Fetched roles from API
  const [availableRoles, setAvailableRoles] = useState<RoleOption[]>([]);
  const roleOptionNames = useMemo(() => availableRoles.map(r => r.name), [availableRoles]);

  useEffect(() => {
    let cancelled = false;
    fetchRoles()
      .then((roles) => {
        if (!cancelled) {
          setAvailableRoles(roles);
        }
      })
      .catch((err) => {
        console.error('[EditResourceBudgetForm] Failed to fetch roles:', err);
        if (!cancelled) {
          setAvailableRoles(FALLBACK_ROLE_OPTIONS.map((name, i) => ({
            id: i + 1,
            name,
            defaultCostRate: 0,
            defaultBillRate: 0,
          })));
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Debug: Log when selectedCell changes
  useEffect(() => {
    console.log('selectedCell changed:', selectedCell);
  }, [selectedCell]);

  // Drag and drop removed - reordering disabled
  // const sensors = useSensors(
  //   useSensor(PointerSensor, {
  //     activationConstraint: {
  //       distance: 8,
  //     },
  //   })
  // );

  // Track if this is the initial mount to prevent marking form as dirty on load
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip onChange on initial mount to prevent false "unsaved changes" alert
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const summary = calculateSummary(byRole, byPerson);
    onChange({
      ...resourceBudget,
      byRole,
      byPerson,
      byPhase,
      summary,
    });
  }, [byRole, byPerson, byPhase]);

  const calculateSummary = (roles: RoleBudget[], people: PersonBudget[]) => {
    const totalBudget = roles.reduce((sum, role) => sum + (role.budgetHours || 0), 0);
    const totalActual = roles.reduce((sum, role) => sum + (role.actualHours || 0), 0);
    const totalRemaining = totalBudget - totalActual;
    const progressPercent = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;

    const totalBudgetCost = roles.reduce((sum, role) => sum + (role.budgetCost || 0), 0);
    const totalActualCost = roles.reduce((sum, role) => sum + (role.actualCost || 0), 0);

    // Calculate billable percentage (assuming all hours are billable for now)
    const billablePercent = totalActual > 0 ? 85 : 0; // Default assumption

    return {
      totalBudget,
      totalActual,
      totalRemaining,
      progressPercent,
      billablePercent,
      targetBillablePercent: 80,
      totalBudgetCost,
      totalActualCost,
    };
  };

  // Role handlers
  const addRole = () => {
    // Get roles already used in this project
    const usedRoles = byRole.map(r => r.role);
    
    // Find the first available role that's not already used
    const firstAvailable = availableRoles.find(r => !usedRoles.includes(r.name));
    
    // If all roles are used, default to empty string (user must select)
    const defaultRole = firstAvailable?.name || '';
    const defaultRoleId = firstAvailable?.id;
    const defaultRate = defaultRole ? getRoleBillRate(defaultRole, 150) : 150;
    
    const newRole: RoleBudget = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      roleId: defaultRoleId,
      role: defaultRole,
      budgetHours: 0,
      allocatedHours: 0,
      actualHours: 0,
      remainingHours: 0,
      percentUsed: 0,
      billRate: defaultRate,
      budgetCost: 0,
      actualCost: 0,
      monthlyHours: Array(totalMonths).fill(0),
    };
    setByRole([...byRole, newRole]);
  };

  const updateRole = (index: number, field: keyof RoleBudget, value: any) => {
    // Prevent modification of billRate field - it should be read-only
    if (field === 'billRate') {
      console.warn('billRate field is read-only and cannot be modified');
      return;
    }
    
    const updated = [...byRole];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // If role changed, update the billRate from Rate Card and set roleId
    if (field === 'role') {
      const matchedRole = availableRoles.find(r => r.name === value);
      if (matchedRole) {
        updated[index].roleId = matchedRole.id;
      }
      const newRate = getRoleBillRate(value, updated[index].billRate);
      updated[index].billRate = newRate;
      
      // Recalculate costs with new rate
      const budgetHours = updated[index].budgetHours;
      const actualHours = updated[index].actualHours;
      updated[index].budgetCost = budgetHours * newRate;
      updated[index].actualCost = actualHours * newRate;
    }
    
    // Recalculate derived fields
    if (field === 'budgetHours' || field === 'actualHours') {
      const budgetHours = field === 'budgetHours' ? value : updated[index].budgetHours;
      const actualHours = field === 'actualHours' ? value : updated[index].actualHours;
      const billRate = updated[index].billRate; // Always use existing billRate
      
      updated[index].remainingHours = budgetHours - actualHours;
      updated[index].percentUsed = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;
      updated[index].budgetCost = budgetHours * billRate;
      updated[index].actualCost = actualHours * billRate;
    }
    
    setByRole(updated);
  };

  const updateMonthlyHours = (roleIndex: number, monthIndex: number, hours: number) => {
    console.log('[updateMonthlyHours] Called:', { roleIndex, monthIndex, hours, totalMonths });
    const updated = [...byRole];
    console.log('[updateMonthlyHours] Current role:', updated[roleIndex]);
    // Ensure monthlyHours array is properly sized
    const existingHours = updated[roleIndex].monthlyHours || [];
    console.log('[updateMonthlyHours] Existing hours:', existingHours);
    const monthlyHours = existingHours.length >= totalMonths 
      ? [...existingHours]
      : [...existingHours, ...Array(totalMonths - existingHours.length).fill(0)];
    console.log('[updateMonthlyHours] Sized monthlyHours:', monthlyHours);
    monthlyHours[monthIndex] = hours;
    console.log('[updateMonthlyHours] After update:', monthlyHours);
    
    // Calculate total from monthly hours
    const totalHours = monthlyHours.reduce((sum, h) => sum + h, 0);
    
    updated[roleIndex] = {
      ...updated[roleIndex],
      monthlyHours,
      budgetHours: totalHours,
    };
    
    // Recalculate derived fields
    const billRate = updated[roleIndex].billRate;
    const actualHours = updated[roleIndex].actualHours;
    updated[roleIndex].remainingHours = totalHours - actualHours;
    updated[roleIndex].percentUsed = totalHours > 0 ? (actualHours / totalHours) * 100 : 0;
    updated[roleIndex].budgetCost = totalHours * billRate;
    updated[roleIndex].actualCost = actualHours * billRate;
    
    setByRole(updated);
    
    // Pattern detection for AI suggestions
    detectPattern(roleIndex, monthIndex, hours, monthlyHours);
  };
  
  // Pattern detection for AI suggestions
  const detectPattern = (roleIndex: number, monthIndex: number, value: number, monthlyHours: number[]) => {
    // Don't suggest if already dismissed
    const suggestionKey = `pattern-${roleIndex}`;
    if (dismissedSuggestions.has(suggestionKey)) return;
    
    // Check if last 3 values are the same and non-zero
    if (monthIndex >= 2 && value > 0) {
      const last3 = [
        monthlyHours[monthIndex - 2],
        monthlyHours[monthIndex - 1],
        monthlyHours[monthIndex]
      ];
      
      const allSame = last3.every(v => v === value);
      
      if (allSame && monthIndex < totalMonths - 1) {
        // Show suggestion to fill remaining months
        setPatternSuggestion({
          roleIndex,
          value,
          fromMonth: monthIndex + 1,
          toMonth: totalMonths - 1
        });
      }
    }
  };
  
  // Fill handlers
  const handleFillClick = (roleIndex: number, monthIndex: number) => {
    const role = byRole[roleIndex];
    const value = role.monthlyHours?.[monthIndex] || 0;
    
    setFillSource({ roleIndex, monthIndex, value });
    setFillDialogOpen(true);
  };
  
  const handleFillApply = (fillType: FillType, toMonth: number, horizontalDirection: 'right' | 'left' = 'right') => {
    if (!fillSource) return;
    
    const { roleIndex, monthIndex, value } = fillSource;
    const updated = [...byRole];
    const monthlyHours = [...(updated[roleIndex].monthlyHours || Array(totalMonths).fill(0))];
    
    // Determine the range based on fill direction
    const startMonth = horizontalDirection === 'right' ? monthIndex : toMonth;
    const endMonth = horizontalDirection === 'right' ? toMonth : monthIndex;
    const monthCount = Math.abs(endMonth - startMonth) + 1;
    
    if (fillType === 'copy') {
      // Copy same value to each month
      for (let i = startMonth; i <= endMonth; i++) {
        monthlyHours[i] = value;
      }
    } else {
      // Distribute equally
      const valuePerMonth = value / monthCount;
      const roundedValue = Math.floor(valuePerMonth * 100) / 100;
      const remainder = value - (roundedValue * monthCount);
      
      for (let i = startMonth; i <= endMonth; i++) {
        const isLast = i === endMonth;
        monthlyHours[i] = isLast ? roundedValue + remainder : roundedValue;
      }
    }
    
    // Calculate total
    const totalHours = monthlyHours.reduce((sum, h) => sum + h, 0);
    
    updated[roleIndex] = {
      ...updated[roleIndex],
      monthlyHours,
      budgetHours: totalHours,
    };
    
    // Recalculate derived fields
    const billRate = updated[roleIndex].billRate;
    const actualHours = updated[roleIndex].actualHours;
    updated[roleIndex].remainingHours = totalHours - actualHours;
    updated[roleIndex].percentUsed = totalHours > 0 ? (actualHours / totalHours) * 100 : 0;
    updated[roleIndex].budgetCost = totalHours * billRate;
    updated[roleIndex].actualCost = actualHours * billRate;
    
    setByRole(updated);
    setFillSource(null);
  };
  
  // Handle pattern suggestion acceptance
  const handlePatternAccept = () => {
    if (!patternSuggestion) return;
    
    const { roleIndex, value, fromMonth, toMonth } = patternSuggestion;
    const updated = [...byRole];
    const monthlyHours = [...(updated[roleIndex].monthlyHours || Array(totalMonths).fill(0))];
    
    // Fill remaining months with the pattern value
    for (let i = fromMonth; i <= toMonth; i++) {
      monthlyHours[i] = value;
    }
    
    // Calculate total
    const totalHours = monthlyHours.reduce((sum, h) => sum + h, 0);
    
    updated[roleIndex] = {
      ...updated[roleIndex],
      monthlyHours,
      budgetHours: totalHours,
    };
    
    // Recalculate derived fields
    const billRate = updated[roleIndex].billRate;
    const actualHours = updated[roleIndex].actualHours;
    updated[roleIndex].remainingHours = totalHours - actualHours;
    updated[roleIndex].percentUsed = totalHours > 0 ? (actualHours / totalHours) * 100 : 0;
    updated[roleIndex].budgetCost = totalHours * billRate;
    updated[roleIndex].actualCost = actualHours * billRate;
    
    setByRole(updated);
    setPatternSuggestion(null);
  };
  
  const handlePatternDismiss = () => {
    if (patternSuggestion) {
      const suggestionKey = `pattern-${patternSuggestion.roleIndex}`;
      setDismissedSuggestions(new Set(dismissedSuggestions).add(suggestionKey));
    }
    setPatternSuggestion(null);
  };

  // Copy/Paste handlers
  // Copy handler - copies entire row based on checkbox selection
  const handleCopy = () => {
    // Cell-level copy (single month)
    if (selectedCell && selectedCell.monthIndex !== null) {
      const role = byRole[selectedCell.roleIndex];
      if (!role) return;

      setClipboard({
        data: (role.monthlyHours || Array(totalMonths).fill(0))[selectedCell.monthIndex],
        type: 'cell',
        sourceRoleIndex: selectedCell.roleIndex,
        sourceMonthIndex: selectedCell.monthIndex,
        isCut: false,
      });
      return;
    }

    // Row-level copy (all months) via checkbox
    if (selectedRows.size === 1) {
      const selectedRoleId = Array.from(selectedRows)[0];
      const roleIndex = byRole.findIndex(r => r.id === selectedRoleId);
      if (roleIndex === -1) return;
      
      const role = byRole[roleIndex];
      setClipboard({
        data: [...(role.monthlyHours || Array(totalMonths).fill(0))],
        type: 'row',
        sourceRoleIndex: roleIndex,
        sourceMonthIndex: null,
        isCut: false,
      });
    }
  };

  // Paste handler - supports both cell and row paste
  const handlePaste = () => {
    if (!clipboard.data) return;

    // Paste to cell (single month)
    if (clipboard.type === 'cell' && selectedCell && selectedCell.monthIndex !== null) {
      const role = byRole[selectedCell.roleIndex];
      if (!role) return;

      const updated = [...byRole];
      const monthlyHours = [...(role.monthlyHours || Array(totalMonths).fill(0))];
      monthlyHours[selectedCell.monthIndex] = clipboard.data as number;
      const totalHours = monthlyHours.reduce((sum, h) => sum + h, 0);

      updated[selectedCell.roleIndex] = {
        ...updated[selectedCell.roleIndex],
        monthlyHours,
        budgetHours: totalHours,
      };

      // Recalculate derived fields
      const billRate = updated[selectedCell.roleIndex].billRate;
      const actualHours = updated[selectedCell.roleIndex].actualHours;
      updated[selectedCell.roleIndex].remainingHours = totalHours - actualHours;
      updated[selectedCell.roleIndex].percentUsed = totalHours > 0 ? (actualHours / totalHours) * 100 : 0;
      updated[selectedCell.roleIndex].budgetCost = totalHours * billRate;
      updated[selectedCell.roleIndex].actualCost = actualHours * billRate;

      setByRole(updated);

      // If it was a cut operation, clear the source cell
      if (clipboard.isCut && clipboard.sourceRoleIndex !== null && clipboard.sourceMonthIndex !== null) {
        const clearedUpdated = [...updated];
        const sourceMonthlyHours = [...(clearedUpdated[clipboard.sourceRoleIndex].monthlyHours || Array(totalMonths).fill(0))];
        sourceMonthlyHours[clipboard.sourceMonthIndex] = 0;
        const sourceTotalHours = sourceMonthlyHours.reduce((sum, h) => sum + h, 0);
        
        clearedUpdated[clipboard.sourceRoleIndex] = {
          ...clearedUpdated[clipboard.sourceRoleIndex],
          monthlyHours: sourceMonthlyHours,
          budgetHours: sourceTotalHours,
        };
        
        // Recalculate source derived fields
        const sourceBillRate = clearedUpdated[clipboard.sourceRoleIndex].billRate;
        const sourceActualHours = clearedUpdated[clipboard.sourceRoleIndex].actualHours;
        clearedUpdated[clipboard.sourceRoleIndex].remainingHours = sourceTotalHours - sourceActualHours;
        clearedUpdated[clipboard.sourceRoleIndex].percentUsed = sourceTotalHours > 0 ? (sourceActualHours / sourceTotalHours) * 100 : 0;
        clearedUpdated[clipboard.sourceRoleIndex].budgetCost = sourceTotalHours * sourceBillRate;
        
        setByRole(clearedUpdated);
        setClipboard({ data: null, type: null, sourceRoleIndex: null, sourceMonthIndex: null, isCut: false });
      }
      return;
    }

    // Paste to row (all months) via checkbox
    if (clipboard.type === 'row' && selectedRows.size === 1) {
      const selectedRoleId = Array.from(selectedRows)[0];
      const roleIndex = byRole.findIndex(r => r.id === selectedRoleId);
      if (roleIndex === -1) return;
      
      const updated = [...byRole];
      const monthlyHours = [...(clipboard.data as number[])];
      const totalHours = monthlyHours.reduce((sum, h) => sum + h, 0);
      
      updated[roleIndex] = {
        ...updated[roleIndex],
        monthlyHours,
        budgetHours: totalHours,
      };
      
      // Recalculate derived fields
      const billRate = updated[roleIndex].billRate;
      const actualHours = updated[roleIndex].actualHours;
      updated[roleIndex].remainingHours = totalHours - actualHours;
      updated[roleIndex].percentUsed = totalHours > 0 ? (actualHours / totalHours) * 100 : 0;
      updated[roleIndex].budgetCost = totalHours * billRate;
      updated[roleIndex].actualCost = actualHours * billRate;
      
      setByRole(updated);
      
      // If it was a cut operation, clear the source
      if (clipboard.isCut && clipboard.sourceRoleIndex !== null) {
        const clearedUpdated = [...updated];
        const emptyMonths = Array(totalMonths).fill(0);
        clearedUpdated[clipboard.sourceRoleIndex] = {
          ...clearedUpdated[clipboard.sourceRoleIndex],
          monthlyHours: emptyMonths,
          budgetHours: 0,
          remainingHours: -clearedUpdated[clipboard.sourceRoleIndex].actualHours,
          percentUsed: 0,
          budgetCost: 0,
        };
        setByRole(clearedUpdated);
        setClipboard({ data: null, type: null, sourceRoleIndex: null, sourceMonthIndex: null, isCut: false });
      }
    }
  };

  const handleDuplicateRole = (roleIndex: number) => {
    const sourceRole = byRole[roleIndex];
    const newRole: RoleBudget = {
      ...sourceRole,
      id: Date.now() + Math.floor(Math.random() * 1000), // Generate unique numeric ID
      role: sourceRole.role, // Will be changed by user
      monthlyHours: [...(sourceRole.monthlyHours || Array(totalMonths).fill(0))],
    };
    
    // Insert below source role
    const updated = [
      ...byRole.slice(0, roleIndex + 1),
      newRole,
      ...byRole.slice(roleIndex + 1),
    ];
    
    setByRole(updated);
  };

  const handleDuplicateMultipleRoles = () => {
    if (selectedRows.size === 0) return;

    // Get all selected role IDs in their current order
    const selectedRoleIds = Array.from(selectedRows);
    
    // Create duplicates for each selected role
    const newRoles: RoleBudget[] = [];
    const updatedByRole = [...byRole];
    
    // Process in reverse order to maintain correct insertion positions
    for (let i = updatedByRole.length - 1; i >= 0; i--) {
      const role = updatedByRole[i];
      if (selectedRoleIds.includes(role.id)) {
        const newRole: RoleBudget = {
          ...role,
          id: Date.now() + Math.floor(Math.random() * 1000) + i, // Generate unique numeric ID
          role: role.role, // Will be changed by user
          monthlyHours: [...(role.monthlyHours || Array(totalMonths).fill(0))],
        };
        // Insert the duplicate right after the original
        updatedByRole.splice(i + 1, 0, newRole);
        newRoles.push(newRole);
      }
    }
    
    setByRole(updatedByRole);
    
    // Clear selection after duplication
    setSelectedRows(new Set());
  };

  // Cut handler - supports both cell and row cut
  const handleCut = () => {
    // Cell-level cut (single month)
    if (selectedCell && selectedCell.monthIndex !== null) {
      const role = byRole[selectedCell.roleIndex];
      if (!role) return;

      setClipboard({
        data: (role.monthlyHours || Array(totalMonths).fill(0))[selectedCell.monthIndex],
        type: 'cell',
        sourceRoleIndex: selectedCell.roleIndex,
        sourceMonthIndex: selectedCell.monthIndex,
        isCut: true,
      });
      return;
    }

    // Row-level cut via checkbox
    if (selectedRows.size === 1) {
      const selectedRoleId = Array.from(selectedRows)[0];
      const roleIndex = byRole.findIndex(r => r.id === selectedRoleId);
      if (roleIndex === -1) return;
      
      const monthlyHours = [...(byRole[roleIndex].monthlyHours || Array(totalMonths).fill(0))];
      setClipboard({
        data: monthlyHours,
        type: 'row',
        sourceRoleIndex: roleIndex,
        sourceMonthIndex: null,
        isCut: true,
      });
    }
  };

  // Shift budget handler
  const handleShiftBudget = (sourceFrom: number, sourceTo: number, destinationStart: number, clearSource: boolean) => {
    if (!shiftSource) return;
    
    const roleIndex = shiftSource.roleIndex;
    const role = byRole[roleIndex];
    const newMonthlyHours = [...(role.monthlyHours || Array(totalMonths).fill(0))];
    
    // Copy values to destination
    const shiftAmount = destinationStart - sourceFrom;
    const sourceMonthlyHours = role.monthlyHours || Array(totalMonths).fill(0);
    for (let i = sourceFrom; i <= sourceTo; i++) {
      const destIndex = i + shiftAmount;
      if (destIndex >= 0 && destIndex < totalMonths) {
        newMonthlyHours[destIndex] = sourceMonthlyHours[i];
      }
    }
    
    // Clear source if requested - but only cells that are NOT in the destination range
    // This handles the case where source and destination ranges overlap
    if (clearSource) {
      const destinationEnd = sourceTo + shiftAmount;
      for (let i = sourceFrom; i <= sourceTo; i++) {
        // Only clear if this cell is NOT in the destination range
        if (i < destinationStart || i > destinationEnd) {
          newMonthlyHours[i] = 0;
        }
      }
    }
    
    // Update role
    const totalHours = newMonthlyHours.reduce((sum, h) => sum + h, 0);
    const updated = [...byRole];
    updated[roleIndex] = {
      ...updated[roleIndex],
      monthlyHours: newMonthlyHours,
      budgetHours: totalHours,
      remainingHours: totalHours - updated[roleIndex].actualHours,
      percentUsed: totalHours > 0 ? (updated[roleIndex].actualHours / totalHours) * 100 : 0,
      budgetCost: totalHours * updated[roleIndex].billRate,
    };
    
    setByRole(updated);
  };

  // Row selection handlers
  const toggleRowSelection = (roleId: number) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(roleId)) {
      newSelection.delete(roleId);
    } else {
      newSelection.add(roleId);
    }
    setSelectedRows(newSelection);
    // Clear cell selection when selecting a row
    setSelectedCell(null);
  };

  const toggleAllRows = () => {
    if (selectedRows.size === byRole.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(byRole.map(role => role.id)));
    }
    // Clear cell selection when selecting all rows
    setSelectedCell(null);
  };

  // Drag and drop handler removed - reordering disabled
  // const handleDragEnd = (event: DragEndEvent) => {
  //   const { active, over } = event;
  //   
  //   if (!over || active.id === over.id) return;
  //   
  //   const oldIndex = byRole.findIndex((role) => role.id === active.id);
  //   const newIndex = byRole.findIndex((role) => role.id === over.id);
  //   
  //   if (oldIndex !== -1 && newIndex !== -1) {
  //     setByRole(arrayMove(byRole, oldIndex, newIndex));
  //   }
  // };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not in an input field
      if (e.target instanceof HTMLInputElement) return;
      
      // Ctrl+C - Copy cell or row
      if (e.ctrlKey && e.key === 'c' && (selectedCell || selectedRows.size === 1)) {
        e.preventDefault();
        handleCopy();
      }
      
      // Ctrl+V - Paste cell or row
      if (e.ctrlKey && e.key === 'v' && (selectedCell || selectedRows.size === 1) && clipboard.data) {
        e.preventDefault();
        handlePaste();
      }
      
      // Ctrl+X - Cut cell or row
      if (e.ctrlKey && e.key === 'x' && (selectedCell || selectedRows.size === 1)) {
        e.preventDefault();
        handleCut();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedRows, selectedCell, clipboard, byRole]);

  const removeRole = (index: number) => {
    const role = byRole[index];
    
    // Validation: Check if role has logged hours
    if (role.actualHours > 0) {
      // Show dialog only, no validation error alert
      setDeleteAttempt({ index, role });
      return;
    }
    
    // If no logged hours, allow removal
    setByRole(byRole.filter((_, i) => i !== index));
    setValidationError(null);
    
    // Clear selection state to prevent stale references to deleted role
    setSelectedRows(new Set());
    setSelectedCell(null);
  };

  const removeMultipleRoles = () => {
    if (selectedRows.size === 0) return;

    // Get all selected role indices
    const selectedRoleIds = Array.from(selectedRows);
    const rolesToDelete = byRole.filter(role => selectedRoleIds.includes(role.id));
    
    // Check if any selected roles have logged hours
    const rolesWithHours = rolesToDelete.filter(role => role.actualHours > 0);
    
    if (rolesWithHours.length > 0) {
      // Show validation error for roles with logged hours
      // Pass all roles with hours to the dialog
      setDeleteAttempt({ index: -1, role: rolesWithHours[0], rolesWithHours });
      return;
    }
    
    // If no logged hours on any selected roles, allow removal
    setByRole(byRole.filter(role => !selectedRoleIds.includes(role.id)));
    setValidationError(null);
    
    // Clear selection state
    setSelectedRows(new Set());
    setSelectedCell(null);
  };
  
  const confirmDeleteRole = () => {
    // This should never be called if validation passes, but included for safety
    if (deleteAttempt && deleteAttempt.role.actualHours === 0) {
      setByRole(byRole.filter((_, i) => i !== deleteAttempt.index));
    }
    setDeleteAttempt(null);
    setValidationError(null);
  };
  
  const cancelDeleteRole = () => {
    setDeleteAttempt(null);
    setValidationError(null);
  };

  // Person handlers
  const addPerson = () => {
    const newPerson: PersonBudget = {
      id: -Date.now(),
      personId: -Date.now(),
      personName: 'Unassigned',
      role: 'Select Role',
      budgetHours: 0,
      actualHours: 0,
      utilisation: 0,
      costRate: 0,
      budgetCost: 0,
      actualCost: 0,
    };
    setByPerson([...byPerson, newPerson]);
  };

  const updatePerson = (index: number, field: keyof PersonBudget, value: any) => {
    const updated = [...byPerson];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // Recalculate derived fields
    if (field === 'budgetHours' || field === 'actualHours' || field === 'costRate') {
      const budgetHours = field === 'budgetHours' ? value : updated[index].budgetHours;
      const actualHours = field === 'actualHours' ? value : updated[index].actualHours;
      const costRate = field === 'costRate' ? value : updated[index].costRate;
      
      updated[index].utilisation = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;
      updated[index].budgetCost = budgetHours * costRate;
      updated[index].actualCost = actualHours * costRate;
    }
    
    setByPerson(updated);
  };

  const removePerson = (index: number) => {
    setByPerson(byPerson.filter((_, i) => i !== index));
  };

  // Phase handlers
  const addPhase = () => {
    const newPhase: PhaseBudgetResource = {
      phaseId: -Date.now(),
      phaseName: 'New Phase',
      budgetHours: 0,
      actualHours: 0,
      remainingHours: 0,
      status: 'not-started',
    };
    setByPhase([...byPhase, newPhase]);
  };

  const updatePhase = (index: number, field: keyof PhaseBudgetResource, value: any) => {
    const updated = [...byPhase];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // Recalculate remaining hours
    if (field === 'budgetHours' || field === 'actualHours') {
      const budgetHours = field === 'budgetHours' ? value : updated[index].budgetHours;
      const actualHours = field === 'actualHours' ? value : updated[index].actualHours;
      updated[index].remainingHours = Math.max(0, budgetHours - actualHours);
    }
    
    setByPhase(updated);
  };

  const removePhase = (index: number) => {
    setByPhase(byPhase.filter((_, i) => i !== index));
  };

  const summary = calculateSummary(byRole, byPerson);

  return (
    <div className="space-y-4">
      {/* Validation Error Alert */}
      {validationError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Cannot Remove Role</AlertTitle>
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteAttempt !== null} onOpenChange={(open) => !open && cancelDeleteRole()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Remove Role with Logged Hours</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                {deleteAttempt?.rolesWithHours && deleteAttempt.rolesWithHours.length > 1 ? (
                  <>
                    The {deleteAttempt.rolesWithHours.length === 2 ? 'roles' : 'roles'}{' '}
                    {deleteAttempt.rolesWithHours.map((r, i) => (
                      <span key={r.id}>
                        <strong>"{r.role}"</strong>
                        {i < deleteAttempt.rolesWithHours!.length - 2 && ', '}
                        {i === deleteAttempt.rolesWithHours!.length - 2 && ' and '}
                      </span>
                    ))}{' '}
                    cannot be removed because {deleteAttempt.rolesWithHours.length === 2 ? 'they have' : 'they have'} logged hours associated with {deleteAttempt.rolesWithHours.length === 2 ? 'them' : 'them'}.
                  </>
                ) : (
                  <>
                    The role <strong>"{deleteAttempt?.role.role}"</strong> cannot be removed because it has logged hours associated with it.
                  </>
                )}
              </p>
              {/* Only show hours/cost details for single role */}
              {(!deleteAttempt?.rolesWithHours || deleteAttempt.rolesWithHours.length === 1) && (
                <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Logged Hours:</span>
                    <span className="font-semibold">{deleteAttempt && formatHours(deleteAttempt.role.actualHours)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Actual Cost:</span>
                    <span className="font-semibold">{deleteAttempt && formatCurrency(deleteAttempt.role.actualCost)}</span>
                  </div>
                </div>
              )}
              <p className="text-sm">
                <strong>To remove {deleteAttempt?.rolesWithHours && deleteAttempt.rolesWithHours.length > 1 ? 'these roles' : 'this role'}, you must:</strong>
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>Reassign the logged hours to another role, or</li>
                <li>Remove the logged time entries from the time tracking system</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                This ensures data integrity between resource budgets and time tracking.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={cancelDeleteRole}>
              I Understand
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="border rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
          <h2 className="text-lg font-semibold">Budget Hours by Role</h2>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  const { roleIndex, monthIndex } = selectedCell;
                  const role = byRole[roleIndex];
                  const hours = role.monthlyHours?.[monthIndex] || 0;
                  // Validation: hours > 0 && more than one column
                  if (hours > 0 && totalMonths > 1) {
                    handleFillClick(roleIndex, monthIndex);
                  }
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (() => {
                if (!selectedCell || selectedCell.monthIndex === null) return true;
                const { roleIndex, monthIndex } = selectedCell;
                const role = byRole[roleIndex];
                if (!role) return true;
                const hours = role.monthlyHours?.[monthIndex] || 0;
                // Disabled if: no value (hours === 0) OR only one column (totalMonths === 1)
                return hours === 0 || totalMonths === 1;
              })()}
              title="Fill from selected cell"
            >
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Fill
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  // Single selection - use original handleDuplicateRole function
                  const selectedRoleId = Array.from(selectedRows)[0];
                  const roleIndex = byRole.findIndex(r => r.id === selectedRoleId);
                  if (roleIndex !== -1) handleDuplicateRole(roleIndex);
                } else if (selectedRows.size > 1) {
                  // Multiple selection - use new handleDuplicateMultipleRoles function
                  handleDuplicateMultipleRoles();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Duplicate ${selectedRows.size} selected roles` : "Duplicate role"}
            >
              <Files className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  // Use the selected cell's role
                  const roleIndex = selectedCell.roleIndex;
                  setShiftSource({ 
                    roleIndex, 
                    roleName: byRole[roleIndex].role 
                  });
                  setShiftDialogOpen(true);
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (byRole[selectedCell.roleIndex]?.monthlyHours?.[selectedCell.monthIndex] ?? 0) === 0}
              title="Shift budget between months (select a monthly cell first)"
            >
              <MoveHorizontal className="h-4 w-4 mr-2" />
              Shift
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  // Single selection - use original removeRole function
                  const selectedRoleId = Array.from(selectedRows)[0];
                  const roleIndex = byRole.findIndex(r => r.id === selectedRoleId);
                  if (roleIndex !== -1) {
                    removeRole(roleIndex);
                  }
                } else if (selectedRows.size > 1) {
                  // Multiple selection - use new removeMultipleRoles function
                  removeMultipleRoles();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Delete ${selectedRows.size} selected roles` : "Delete selected role"}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={byRole.length >= roleOptionNames.length ? 0 : undefined}>
                    <Button 
                      onClick={addRole} 
                      size="sm" 
                      variant="outline"
                      disabled={roleOptionNames.length > 0 && byRole.length >= roleOptionNames.length}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Role
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{roleOptionNames.length > 0 && byRole.length >= roleOptionNames.length ? "No more available roles to add" : "Add a new role"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="p-0">
          {byRole.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No roles yet. Click "Add Role" to get started.
            </div>
          ) : (
            <>
              {/* 3-Column Layout: Left (sticky) + Middle (scrollable) + Right (sticky) */}
              <div className="flex border rounded-md">
              {/* LEFT SECTION: Checkbox + Role (Fixed, No Scroll) */}
              <div className="flex-shrink-0 border-r">
                <Table className="border-collapse">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px] bg-background">
                        <Checkbox
                          checked={selectedRows.size === byRole.length && byRole.length > 0}
                          onCheckedChange={toggleAllRows}
                          aria-label="Select all rows"
                        />
                      </TableHead>
                      <TableHead className="w-[200px] bg-background">Role</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byRole.map((role, index) => (
                      <TableRow key={role.id}>
                        <TableCell className="p-2 w-[40px]">
                          <Checkbox
                            checked={selectedRows.has(role.id)}
                            onCheckedChange={() => toggleRowSelection(role.id)}
                            aria-label={`Select ${role.role}`}
                          />
                        </TableCell>
                        <TableCell className="p-2 w-[200px]">
                          <Combobox
                            options={(() => {
                              // Get roles already used by OTHER rows in this project
                              const usedRoles = byRole
                                .filter((r, i) => i !== index) // Exclude current row
                                .map(r => r.role);
                              
                              // Filter out already-used roles from options
                              const options = roleOptionNames
                                .filter(option => !usedRoles.includes(option))
                                .map((option) => ({
                                  value: option,
                                  label: option,
                                }));
                              
                              // Always include the current role if it's set (even if not in fetched roles)
                              if (role.role && !options.some(o => o.value === role.role)) {
                                options.unshift({ value: role.role, label: role.role });
                              }
                              return options;
                            })()}
                            value={role.role}
                            onValueChange={(value) => updateRole(index, 'role', value)}
                            placeholder="Select role"
                            searchPlaceholder="Search roles..."
                            emptyMessage="No role found."
                            className="h-9"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell className="p-2 w-[40px] bg-background"></TableCell>
                      <TableCell className="p-2 w-[200px] font-semibold bg-background">Project Total Hours</TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>

              {/* MIDDLE SECTION: Monthly Columns (Scrollable) */}
              <div 
                className="flex-1 overflow-x-auto overflow-y-visible [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-slate-500"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgb(203 213 225) rgb(241 245 249)',
                }}
              >
                <Table 
                  className="border-collapse"
                  style={{
                    // Make table width match the number of months (100px per month)
                    width: `${yearMonths.length * 100}px`,
                    minWidth: '100%'
                  }}
                >
                  <TableHeader>
                    <TableRow>
                      {yearMonths.map((monthData) => (
                        <TableHead key={`${monthData.year}-${monthData.month}`} className="w-[100px] text-center">
                          {MONTHS[monthData.month]}/{String(monthData.year).slice(-2)}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byRole.map((role, index) => (
                      <TableRow key={role.id}>
                        {visibleMonths.map((monthIndex) => {
                          // Ensure monthlyHours array is the correct size
                          const existingHours = role.monthlyHours || [];
                          const monthlyHours = Array(totalMonths).fill(0).map((_, i) => existingHours[i] || 0);
                          const isSelected = selectedCell?.roleIndex === index && selectedCell?.monthIndex === monthIndex;

                          return (
                            <TableCell key={monthIndex} className="p-2 w-[100px]">
                              <NumberInput
                                value={monthlyHours[monthIndex]}
                                onChange={(value) => updateMonthlyHours(index, monthIndex, value)}
                                maxValue={999999.99}
                                onFocus={() => setSelectedCell({ roleIndex: index, monthIndex })}
                                className={cn(
                                  "w-full h-9 px-2 text-sm text-center",
                                  isSelected && "ring-2 ring-ring"
                                )}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      {visibleMonths.map((monthIndex) => {
                        const monthTotal = byRole.reduce((sum, role) => {
                          const existingHours = role.monthlyHours || [];
                          const hours = existingHours[monthIndex] || 0;
                          return sum + hours;
                        }, 0);
                        return (
                          <TableCell key={monthIndex} className="p-2 w-[100px]">
                            <Input
                              value={formatNumber(monthTotal)}
                              readOnly
                              className="w-full h-9 px-2 text-sm text-center font-semibold bg-muted cursor-default"
                              title={formatNumber(monthTotal)}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>

              {/* RIGHT SECTION: Project Total (Fixed, No Scroll) */}
              <div className="flex-shrink-0 border-l-2">
                <Table className="border-collapse">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px] text-center font-semibold bg-primary/10">Project Total Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byRole.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell className="p-2 w-[120px]">
                          <Input
                            value={`${formatNumber(role.budgetHours)}h`}
                            readOnly
                            className="w-full h-9 px-2 text-sm text-center font-semibold bg-primary/10 cursor-default"
                            title={`${formatNumber(role.budgetHours)}h`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell className="p-2 w-[120px] bg-background">
                        <Input
                          value={`${formatNumber(byRole.reduce((sum, role) => sum + role.budgetHours, 0))}h`}
                          readOnly
                          className="w-full h-9 px-2 text-sm text-center font-bold bg-primary/20 cursor-default"
                          title={`${formatNumber(byRole.reduce((sum, role) => sum + role.budgetHours, 0))}h`}
                        />
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Pattern Suggestion */}
      {patternSuggestion && (
        <AISuggestion
          type="info"
          title="Pattern Detected"
          message={`Fill remaining months with ${patternSuggestion.value.toFixed(2)} hours?`}
          details={`You've entered ${patternSuggestion.value.toFixed(2)} hours for 3 consecutive months. Would you like to fill ${MONTHS[patternSuggestion.fromMonth]} through ${MONTHS[patternSuggestion.toMonth]} with the same value?`}
          confidence={95}
          onAccept={handlePatternAccept}
          onDismiss={handlePatternDismiss}
          acceptLabel="Fill Remaining Months"
          dismissLabel="No Thanks"
        />
      )}

      {/* Fill Sheet (with expand to modal option) */}
      <FillSheet
        open={fillDialogOpen}
        onOpenChange={setFillDialogOpen}
        sourceValue={fillSource?.value || 0}
        sourceMonth={fillSource?.monthIndex || 0}
        direction="right"
        onApply={handleFillApply}
        totalMonths={totalMonths}
        monthLabels={yearMonths.map(ym => `${MONTHS[ym.month]}/${String(ym.year).slice(-2)}`)}
        budgetType="resource"
      />
      
      {/* Original Fill Dialog (preserved for reference, not used) */}
      {/* <FillDialog
        open={fillDialogOpen}
        onOpenChange={setFillDialogOpen}
        sourceValue={fillSource?.value || 0}
        sourceMonth={fillSource?.monthIndex || 0}
        direction="right"
        onApply={handleFillApply}
      /> */}

      {/* Shift Budget Sheet */}
      {shiftSource && (
        <ShiftBudgetSheet
          open={shiftDialogOpen}
          onOpenChange={setShiftDialogOpen}
          roleName={shiftSource.roleName}
          monthlyHours={byRole[shiftSource.roleIndex]?.monthlyHours || Array(totalMonths).fill(0)}
          selectedMonthIndex={selectedCell?.monthIndex ?? undefined}
          onShift={handleShiftBudget}
          totalMonths={totalMonths}
          monthLabels={yearMonths.map(ym => `${MONTHS[ym.month]}/${String(ym.year).slice(-2)}`)}
          budgetType="resource"
        />
      )}

    </div>
  );
}
